import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertTriangle,
  Download,
  Loader2,
  Plus,
  Settings,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { usePhoneNumbers } from '@/hooks/usePhoneNumbers';
import { useToast } from '@/hooks/use-toast';
import { PhoneNumber } from '@/types/phoneNumber';
import { PhoneNumberPurchaser } from './PhoneNumberPurchaser';

interface VapiAccount {
  keyName: string;
  friendlyName: string;
}

interface AddPhoneNumberDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called after a number is successfully imported or purchased. */
  onNumberAdded?: () => void;
}

// Capabilities come back either as an array or as a map of flags.
const normalizeCapabilities = (capabilities: any): string[] => {
  if (!capabilities) return [];
  if (Array.isArray(capabilities)) return capabilities;
  if (typeof capabilities === 'object') {
    return Object.entries(capabilities)
      .filter(([_, enabled]) => enabled)
      .map(([capability]) => capability);
  }
  return [];
};

type Mode = 'choose' | 'import';

export function AddPhoneNumberDialog({
  isOpen,
  onClose,
  onNumberAdded,
}: AddPhoneNumberDialogProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const {
    formatPhoneNumber,
    fetchTwilioNumbers,
    importSelectedNumbers,
  } = usePhoneNumbers();

  const [vapiAccounts, setVapiAccounts] = useState<VapiAccount[]>([]);
  const [vapiKeyName, setVapiKeyName] = useState('');
  const [vapiLoading, setVapiLoading] = useState(true);
  const [vapiError, setVapiError] = useState<string | null>(null);

  const [mode, setMode] = useState<Mode>('choose');
  const [showPurchaser, setShowPurchaser] = useState(false);
  const [twilioNumbers, setTwilioNumbers] = useState<any[]>([]);
  const [selectedNumber, setSelectedNumber] = useState<string | null>(null);
  const [friendlyName, setFriendlyName] = useState('');
  const [importLoading, setImportLoading] = useState(false);

  // VAPI is required: imported/purchased numbers are registered with VAPI as
  // part of the flow, so resolve the available accounts before enabling either
  // path. Loaded on open so newly-added keys are picked up without a reload.
  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;

    const loadVapiAccounts = async () => {
      setVapiLoading(true);
      setVapiError(null);

      try {
        const { data, error } = await supabase.functions.invoke(
          'manage-api-keys',
          { body: { action: 'get', filter_provider: 'vapi' } },
        );

        if (error) throw error;
        if (cancelled) return;

        const accounts: VapiAccount[] = (data?.keys || [])
          .filter((k: any) => !!k.decrypted_value)
          .map((k: any) => ({
            keyName: k.key_name,
            friendlyName: k.friendlyName || (k.key_name === 'api_key' ? 'Primary Account' : k.key_name),
          }))
          .sort((a: VapiAccount, b: VapiAccount) => {
            if (a.keyName === 'api_key') return -1;
            if (b.keyName === 'api_key') return 1;
            return 0;
          });

        setVapiAccounts(accounts);
        setVapiKeyName((current) => current || accounts[0]?.keyName || '');
      } catch (err) {
        if (cancelled) return;
        setVapiError(
          err instanceof Error ? err.message : 'Failed to load VAPI accounts',
        );
      } finally {
        if (!cancelled) setVapiLoading(false);
      }
    };

    loadVapiAccounts();

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const resetState = () => {
    setMode('choose');
    setTwilioNumbers([]);
    setSelectedNumber(null);
    setFriendlyName('');
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const hasVapi = vapiAccounts.length > 0;
  const blocked = !vapiLoading && !hasVapi;

  const handleImportClick = async () => {
    try {
      setImportLoading(true);
      const numbers = await fetchTwilioNumbers();
      setTwilioNumbers(numbers);
      setMode('import');
    } catch (error) {
      // fetchTwilioNumbers surfaces its own toast
    } finally {
      setImportLoading(false);
    }
  };

  const handleImportSubmit = async () => {
    if (!selectedNumber) {
      toast({
        title: 'No Number Selected',
        description: 'Please select a number to import.',
        variant: 'destructive',
      });
      return;
    }

    if (!vapiKeyName) {
      toast({
        title: 'VAPI Account Required',
        description: 'Select a VAPI account before importing.',
        variant: 'destructive',
      });
      return;
    }

    setImportLoading(true);
    try {
      const numbers = twilioNumbers
        .filter((n) => n.phoneNumber === selectedNumber)
        .map((n) => (friendlyName.trim() ? { ...n, friendlyName: friendlyName.trim() } : n));

      const result = await importSelectedNumbers(numbers, vapiKeyName);

      if (result.imported > 0) {
        toast({
          title: 'Number Added',
          description: `Successfully imported ${formatPhoneNumber(selectedNumber)}.`,
        });
        onNumberAdded?.();
        handleClose();
      } else {
        // Keep the dialog open so the user can retry or pick another number.
        const errorMessages = result.errors || ['No numbers were imported'];
        toast({
          title: 'Import Failed',
          description: errorMessages.join(', '),
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Import Failed',
        description:
          error instanceof Error ? error.message : 'Failed to import number',
        variant: 'destructive',
      });
    } finally {
      setImportLoading(false);
    }
  };

  const handlePurchaseComplete = (_newNumber: PhoneNumber) => {
    setShowPurchaser(false);
    onNumberAdded?.();
    handleClose();
  };

  return (
    <>
      <Dialog
        open={isOpen && !showPurchaser}
        onOpenChange={(open) => !open && handleClose()}
      >
        <DialogContent className='max-w-2xl max-h-[90vh] overflow-y-auto'>
          <DialogHeader>
            <DialogTitle>Add a Phone Number</DialogTitle>
            <DialogDescription>
              Import a number you already own in Twilio, or buy a new one.
            </DialogDescription>
          </DialogHeader>

          {vapiLoading ? (
            <div className='flex items-center justify-center py-8'>
              <Loader2 className='w-5 h-5 animate-spin mr-2' />
              <span className='text-sm text-muted-foreground'>
                Checking your VAPI setup...
              </span>
            </div>
          ) : blocked ? (
            // VAPI is a hard requirement server-side, so explain the block and
            // offer the fix rather than failing at submit time.
            <div className='space-y-4'>
              <div className='flex gap-3 p-4 border border-destructive/50 rounded-lg bg-destructive/5'>
                <AlertTriangle className='w-5 h-5 text-destructive shrink-0 mt-0.5' />
                <div className='space-y-1'>
                  <p className='text-sm font-medium text-destructive'>
                    VAPI setup required
                  </p>
                  <p className='text-sm text-muted-foreground'>
                    {vapiError
                      ? `We couldn't load your VAPI accounts: ${vapiError}`
                      : 'Phone numbers are registered with VAPI when they are added, so you need to connect a VAPI API key before importing or buying a number.'}
                  </p>
                </div>
              </div>

              <div className='space-y-2'>
                <Button disabled className='w-full' variant='default'>
                  <Download className='w-4 h-4 mr-2' />
                  Import from Twilio
                </Button>
                <Button disabled className='w-full' variant='outline'>
                  <Plus className='w-4 h-4 mr-2' />
                  Buy a New Number
                </Button>
              </div>

              <div className='flex justify-end gap-3 pt-4 border-t'>
                <Button variant='outline' onClick={handleClose}>
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    handleClose();
                    navigate('/api-settings');
                  }}
                >
                  <Settings className='w-4 h-4 mr-2' />
                  Go to API Settings
                </Button>
              </div>
            </div>
          ) : mode === 'choose' ? (
            <div className='space-y-4'>
              {vapiAccounts.length > 1 ? (
                <div className='space-y-2'>
                  <Label htmlFor='vapi-account'>VAPI Account</Label>
                  <Select value={vapiKeyName} onValueChange={setVapiKeyName}>
                    <SelectTrigger id='vapi-account'>
                      <SelectValue placeholder='Select VAPI account' />
                    </SelectTrigger>
                    <SelectContent>
                      {vapiAccounts.map((account) => (
                        <SelectItem key={account.keyName} value={account.keyName}>
                          {account.friendlyName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className='text-sm text-muted-foreground'>
                    The number will be registered with this VAPI account.
                  </p>
                </div>
              ) : (
                <div className='flex items-center gap-2 p-3 bg-muted rounded-lg'>
                  <Settings className='w-4 h-4 text-muted-foreground' />
                  <div className='text-sm text-muted-foreground'>
                    Using VAPI Account: {vapiAccounts[0]?.friendlyName}
                  </div>
                </div>
              )}

              <div className='space-y-2'>
                <Button
                  onClick={handleImportClick}
                  disabled={importLoading}
                  className='w-full'
                  variant='default'
                >
                  {importLoading ? (
                    <Loader2 className='w-4 h-4 mr-2 animate-spin' />
                  ) : (
                    <Download className='w-4 h-4 mr-2' />
                  )}
                  Import from Twilio
                </Button>
                <p className='text-xs text-muted-foreground px-1'>
                  Already own a number in your Twilio account? Import it here.
                </p>

                <Button
                  onClick={() => setShowPurchaser(true)}
                  className='w-full'
                  variant='outline'
                >
                  <Plus className='w-4 h-4 mr-2' />
                  Buy a New Number
                </Button>
                <p className='text-xs text-muted-foreground px-1'>
                  Search for and purchase a new number through Twilio.
                </p>
              </div>
            </div>
          ) : (
            <div className='space-y-4'>
              <div className='flex items-center gap-2 p-3 bg-muted rounded-lg'>
                <Settings className='w-4 h-4 text-muted-foreground' />
                <div className='text-sm text-muted-foreground'>
                  Using VAPI Account:{' '}
                  {vapiAccounts.find((a) => a.keyName === vapiKeyName)?.friendlyName ||
                    vapiKeyName}
                </div>
              </div>

              <div className='space-y-2'>
                <Label htmlFor='friendlyName'>Friendly Name (optional)</Label>
                <Input
                  id='friendlyName'
                  placeholder='e.g., AI Sales Assistant'
                  value={friendlyName}
                  onChange={(e) => setFriendlyName(e.target.value)}
                />
              </div>

              {twilioNumbers.length > 0 ? (
                <div className='space-y-3'>
                  <Label className='text-sm font-medium'>
                    Available Numbers ({twilioNumbers.length})
                  </Label>

                  <div className='border rounded-lg max-h-60 overflow-y-auto'>
                    {twilioNumbers.map((number) => (
                      <label
                        key={number.phoneNumber}
                        className='flex items-center gap-3 p-3 border-b last:border-b-0 hover:bg-muted/50 cursor-pointer'
                      >
                        <input
                          type='radio'
                          name='selectedNumber'
                          checked={selectedNumber === number.phoneNumber}
                          onChange={() => setSelectedNumber(number.phoneNumber)}
                          className='w-4 h-4 text-primary bg-background border-2 border-border focus:ring-2 focus:ring-primary'
                        />
                        <div>
                          <div className='font-medium'>
                            {formatPhoneNumber(number.phoneNumber)}
                          </div>
                          {number.friendlyName && (
                            <div className='text-sm text-muted-foreground'>
                              {number.friendlyName}
                            </div>
                          )}
                          <div className='flex gap-1 mt-1'>
                            {normalizeCapabilities(number.capabilities).map(
                              (capability: string) => (
                                <Badge
                                  key={capability}
                                  variant='secondary'
                                  className='text-xs'
                                >
                                  {capability.toUpperCase()}
                                </Badge>
                              ),
                            )}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              ) : (
                <div className='text-center py-8 space-y-2'>
                  <p className='text-muted-foreground'>
                    No numbers found in your Twilio account.
                  </p>
                  <p className='text-sm text-muted-foreground'>
                    You can buy a new number instead.
                  </p>
                </div>
              )}

              <div className='flex justify-end gap-3 pt-4 border-t'>
                <Button variant='outline' onClick={() => setMode('choose')}>
                  Back
                </Button>
                <Button
                  onClick={handleImportSubmit}
                  disabled={!selectedNumber || importLoading}
                >
                  {importLoading ? (
                    <>
                      <Loader2 className='w-4 h-4 mr-2 animate-spin' />
                      Importing...
                    </>
                  ) : (
                    'Import Selected Number'
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <PhoneNumberPurchaser
        isOpen={showPurchaser}
        onClose={() => setShowPurchaser(false)}
        onPurchaseComplete={handlePurchaseComplete}
        provider='twilio'
        selectedVapiKeyName={vapiKeyName}
      />
    </>
  );
}
